import type { ReactNode } from 'react';

import styles from './AgentRepliesSection.module.css';
import { LandingImage } from './LandingImage';
import { PrimaryCta } from './PrimaryCta';

type AgentRepliesSectionProps = {
	onCta: () => void;
};

type Card = {
	src: string;
	alt: string;
	title: string;
	body: string;
	large?: boolean;
	overlay: ReactNode;
};

const CursorIcon = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		className={styles.cursor}
		aria-hidden="true"
	>
		<path
			d="M5 3 L5 19 L9 15 L11.5 21 L13.5 20 L11 14 L17 14 Z"
			fill="#111"
			stroke="#fff"
			strokeWidth="1"
			strokeLinejoin="round"
		/>
	</svg>
);

const OpenRatesOverlay = () => (
	<div className={styles.cardOverlay}>
		<div className={styles.pill1}>
			<span className={`${styles.pillLabel} ${styles.pillLabel1}`}>
				Your March update
			</span>
			<span className={`${styles.pillStat} ${styles.pillStat1}`}>
				18% Opens
			</span>
		</div>
		<div className={styles.pill2}>
			<span className={`${styles.pillLabel} ${styles.pillLabel2}`}>
				3 things we learned in Q1
			</span>
			<span className={`${styles.pillStat} ${styles.pillStat2}`}>
				34% Opens
			</span>
		</div>
		<div className={styles.sendWinner}>Send winner</div>
		<CursorIcon />
	</div>
);

const PersonCard = ({
	variant,
	name,
	role,
	photo,
}: {
	variant: 'center' | 'left' | 'right';
	name: string;
	role: string;
	photo: string;
}) => {
	const cardClass =
		variant === 'center'
			? styles.personCardCenter
			: variant === 'left'
				? styles.personCardLeft
				: styles.personCardRight;
	const small = variant !== 'center';
	return (
		<div className={`${styles.personCard} ${cardClass}`}>
			<div
				className={`${styles.personPhoto}
					${small ? styles.personPhotoSmall : ''}`}
			>
				<LandingImage src={photo} alt="" className={styles.personPhotoImg} />
			</div>
			<p
				className={`${styles.personName} ${small ? styles.personNameSmall : ''}`}
			>
				{name}
			</p>
			<p
				className={`${styles.personRole} ${small ? styles.personRoleSmall : ''}`}
			>
				{role}
			</p>
			<div
				className={`${styles.personSkeleton}
					${small ? styles.personSkeletonSm1 : styles.personSkeleton1}`}
			/>
			<div
				className={`${styles.personSkeleton}
					${small ? styles.personSkeletonSm2 : styles.personSkeleton2}`}
			/>
		</div>
	);
};

const NewClientsOverlay = () => (
	<div className={styles.cardOverlay}>
		<PersonCard
			variant="left"
			name="Eric McVicker"
			role="VP, Marketing"
			photo="/landing/replies-person-eric.png"
		/>
		<PersonCard
			variant="right"
			name="Amy Paulson"
			role="SVP, Brand Marketing"
			photo="/landing/replies-person-amy.png"
		/>
		<PersonCard
			variant="center"
			name="Sarah Mason"
			role="VP, Marketing"
			photo="/landing/replies-person-sarah.png"
		/>
		<div className={styles.icpPill}>ICP Match: 94%</div>
	</div>
);

const FiveStarOverlay = () => (
	<div className={styles.cardOverlay}>
		<div className={styles.stars}>
			<div className={styles.starsInner}>
				<LandingImage
					src="/landing/replies-stars.svg"
					alt=""
					className={styles.starsImg}
				/>
			</div>
		</div>
		<div className={styles.quotePill}>
			&ldquo;Best facial I&rsquo;ve had in years. The team took time to actually
			understand my skin before recommending anything.&rdquo;
		</div>
	</div>
);

const CalendarOverlay = () => (
	<div className={styles.cardOverlay}>
		<div className={`${styles.dayBox} ${styles.dayBoxLeft}`}>
			<span className={styles.dayLabel}>Sunday</span>
		</div>
		<div className={`${styles.dayBox} ${styles.dayBoxRight}`}>
			<span className={styles.dayLabel}>Tuesday</span>
		</div>
		<div className={`${styles.dayBox} ${styles.dayBoxCenter}`}>
			<span className={styles.dayLabel}>Monday</span>
		</div>
		<div className={styles.bottle}>
			<LandingImage
				src="/landing/replies-bottle.png"
				alt=""
				className={styles.bottleImg}
			/>
		</div>
	</div>
);

const MessagesOverlay = () => (
	<div className={styles.cardOverlay}>
		<div className={styles.messagesContainer}>
			<div className={styles.messagesHeader}>
				<span>High intent</span>
				<span className={styles.messagesHeaderDot} />
				<span>Drafted reply</span>
			</div>
			<div className={styles.messagesList}>
				{[
					{
						src: '/landing/replies-chat-av-1.png',
						sk: styles.messageSkeleton1,
					},
					{
						src: '/landing/replies-chat-av-2.png',
						sk: styles.messageSkeleton2,
					},
					{
						src: '/landing/replies-chat-av-3.png',
						sk: styles.messageSkeleton3,
					},
					{
						src: '/landing/replies-chat-av-4.png',
						sk: styles.messageSkeleton4,
					},
				].map((row, i) => (
					<div key={i} className={styles.messageRow}>
						<span className={styles.messageAvatarWrap}>
							<LandingImage
								src={row.src}
								alt=""
								className={styles.messageAvatar}
							/>
						</span>
						<span className={`${styles.messageSkeleton} ${row.sk}`} />
					</div>
				))}
			</div>
		</div>
	</div>
);

const WinbackOverlay = () => (
	<div className={styles.cardOverlay}>
		<div className={styles.winbackHeader}>
			<p className={styles.winbackName}>Alex Young</p>
			<p className={styles.winbackMeta}>Last booking: 75 days ago</p>
		</div>
		<div className={styles.winbackStatus}>
			<div className={`${styles.winbackRow} ${styles.winbackRow1}`}>
				<span className={styles.winbackDot} />
				<p className={styles.winbackText}>Lapsed flag triggered</p>
			</div>
			<span
				className={`${styles.winbackConnector} ${styles.winbackConnector1}`}
			/>
			<div className={`${styles.winbackRow} ${styles.winbackRow2}`}>
				<span className={styles.winbackDot} />
				<p className={styles.winbackText}>Win-back SMS sent</p>
			</div>
			<span
				className={`${styles.winbackConnector} ${styles.winbackConnector2}`}
			/>
			<div className={`${styles.winbackRow} ${styles.winbackRow3}`}>
				<span className={`${styles.winbackDot} ${styles.winbackDotActive}`} />
				<p className={`${styles.winbackText} ${styles.winbackTextActive}`}>
					Booked Tuesday 6pm
				</p>
			</div>
		</div>
	</div>
);

const ROW_ONE: Card[] = [
	{
		src: '/landing/replies-open-rates.png',
		alt: 'Email open rate chart trending upward',
		title: 'Higher open rates',
		body: "Rewrites subject lines based on what's worked in your past sends. A/B tests new variants weekly and retire the ones your audience ignores.",
		large: true,
		overlay: <OpenRatesOverlay />,
	},
	{
		src: '/landing/replies-new-clients.png',
		alt: 'New client pipeline view',
		title: 'More new clients',
		body: 'Sources ideal customers every day. Pulls real context from their LinkedIn or company site, then drafts personalized outreach.',
		overlay: <NewClientsOverlay />,
	},
	{
		src: '/landing/replies-five-star.png',
		alt: 'Five-star review notification',
		title: 'Faster five-star reviews',
		body: 'Texts happy customers right after their appointment. Drafts measured replies to negative reviews by 7am and keeps your local SEO climbing.',
		overlay: <FiveStarOverlay />,
	},
];

const ROW_TWO: Card[] = [
	{
		src: '/landing/replies-calendar.png',
		alt: 'Daily content calendar view',
		title: 'A daily content calendar',
		body: "Monitors what's hitting in your industry. Drafts a week of posts in your brand voice and queues them for your approval.",
		overlay: <CalendarOverlay />,
	},
	{
		src: '/landing/replies-messages.png',
		alt: 'Inbox showing every message answered',
		title: 'Every message, answered',
		body: 'Sorts every DM, comment, and form as it lands. Writes responses in your brand voice and flags high-intent leads before they go cold.',
		overlay: <MessagesOverlay />,
	},
	{
		src: '/landing/replies-winback.png',
		alt: 'Win-back campaign chart',
		title: 'Lapsed customers, won back',
		body: 'Segments your customer base. Proposes personalized re-engagement campaigns across email, SMS, and voice, and reactivates the people who used to buy.',
		large: true,
		overlay: <WinbackOverlay />,
	},
];

const CardItem = ({ card }: { card: Card }) => (
	<li className={styles.card}>
		<div
			className={`${styles.cardImageFrame}
				${card.large ? '' : styles.cardImageFrameSmall}`}
		>
			<LandingImage
				src={card.src}
				alt={card.alt}
				className={styles.cardImage}
			/>
			{card.overlay}
		</div>
		<div className={styles.cardBody}>
			<h3 className={styles.cardTitle}>{card.title}</h3>
			<p className={styles.cardCopy}>{card.body}</p>
		</div>
	</li>
);

export const AgentRepliesSection = ({ onCta }: AgentRepliesSectionProps) => (
	<section className={styles.section}>
		<div className={styles.inner}>
			<ul className={`${styles.row} ${styles.rowLargeLeft}`}>
				{ROW_ONE.map((card) => (
					<CardItem key={card.title} card={card} />
				))}
			</ul>
			<ul className={`${styles.row} ${styles.rowLargeRight}`}>
				{ROW_TWO.map((card) => (
					<CardItem key={card.title} card={card} />
				))}
			</ul>
			<div className={styles.cta}>
				<PrimaryCta onClick={onCta} variant="dark" label="Meet your teammate" />
			</div>
		</div>
	</section>
);
